import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TextField,
  Box,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import OrderServices from '../../services/orders';
import QuantitySelector from '../../components/QuantitySelector/QuantitySelector';

export default function SkuCorrection({
  isOpen,
  handleClose,
  onSubmit,
  sku,
  skuQty,
  existingCorrection = null,
}) {
  const [correction, setCorrection] = useState({
    sku: '',
    quantity: 0,
    color: '',
    theme: '',
    vendor: '',
    description: '',
  });
  const [error, setError] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isLoadingSku, setIsLoadingSku] = useState(false);
  const division = useSelector(
    (state) => state.product.previousSearchValues.division
  );

  // Populate modal with existing correction data when it opens
  useEffect(() => {
    if (isOpen && existingCorrection) {
      setCorrection({
        sku: existingCorrection.sku || '',
        quantity: existingCorrection.quantity || 0,
        color: existingCorrection.color || '',
        theme: existingCorrection.theme || '',
        vendor: existingCorrection.vendor || '',
        description: existingCorrection.description || '',
      });
      setError(false);
      setIsLoadingSku(false);
    } else if (isOpen && !existingCorrection) {
      setCorrection({
        sku: '',
        quantity: 0,
        color: '',
        theme: '',
        vendor: '',
        description: '',
      });
      setError(false);
      setIsLoadingSku(false);
    }
  }, [isOpen, existingCorrection]);

  const handleOpenSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleBlur = () => {
    handleGetSkuInfo();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.target.blur();
    }
  };

  const handleCancel = () => {
    setCorrection({
      sku: '',
      quantity: 0,
      color: '',
      theme: '',
      vendor: '',
      description: '',
    });
    setError(false);
    setIsLoadingSku(false);
    handleClose();
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleGetSkuInfo = async () => {
    setIsLoadingSku(true);
    const skuInfo = await OrderServices.getSkuInfo(
      division,
      correction.sku.trim()
    );

    if (!skuInfo.IsSuccessful) {
      handleOpenSnackbar('Sku is invalid. Please enter a valid sku.');
      setCorrection({
        sku: '',
        color: '',
        theme: '',
        vendor: '',
        description: '',
        quantity: 0,
      });
      setIsLoadingSku(false);
      return;
    }
    setCorrection((prev) => ({
      ...prev,
      color: skuInfo.pmRec.Color,
      theme: skuInfo.pmRec.Theme,
      vendor: skuInfo.pmRec.VendorCode,
      description: skuInfo.pmRec.VendorDescription,
    }));
    setIsLoadingSku(false);
  };

  const handleSubmit = () => {
    if (isLoadingSku) {
      handleOpenSnackbar('Please wait for SKU validation to complete');
      return;
    }
    if (!correction.sku || correction.quantity === 0) {
      setError(true);
      handleOpenSnackbar('Please enter a new sku and a quantity');
      return;
    }
    if (correction.sku.length > 8 || correction.sku.length < 8) {
      setError(true);
      handleOpenSnackbar('A sku can only be 8 digits');
      return;
    }
    if (!correction.vendor) {
      setError(true);
      handleOpenSnackbar('Please enter a valid sku');
      return;
    }
    onSubmit(correction);
  };

  const handleIncrementQty = () => {
    setCorrection((prev) => ({
      ...prev,
      quantity:
        prev.quantity === skuQty
          ? prev.quantity
          : prev.quantity + 1,
    }));
    setError(false);
  };

  const handleDecrementQty = () => {
    setCorrection((prev) => ({
      ...prev,
      quantity:
        prev.quantity === 0
          ? prev.quantity
          : prev.quantity - 1,
    }));
    setError(false);
  };

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose}>
        <DialogTitle component="div">
          <Typography variant="h3" sx={{ marginY: 1 }}>
            Sku Correction
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            sx={{ width: '100%', gap: 2, mb: 2 }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            >
              <Typography>Current Sku</Typography>
              <Divider orientation="horizontal" flexItem />
              <Typography fontWeight="bold">{sku}</Typography>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            >
              <Typography>Current Quantity</Typography>
              <Divider orientation="horizontal" flexItem />
              <Typography fontWeight="bold">{skuQty}</Typography>
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            sx={{ width: '100%', gap: 2 }}
          >
            <Typography>New Sku:</Typography>
            <TextField
              value={correction.sku}
              onChange={(e) => {
                setCorrection((prev) => ({ 
                  ...prev, 
                  sku: e.target.value.trim(),
                  color: '',
                  theme: '',
                  vendor: '',
                  description: '',
                }));
                setError(false);
                setIsLoadingSku(false);
              }}
              error={error}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              InputProps={{
                sx: { height: '40px' },
              }}
            />
            <Box display="flex" alignItems="center" justifyContent="center">
              <QuantitySelector
                increment={() => {
                  handleIncrementQty();
                }}
                decrement={() => {
                  handleDecrementQty();
                }}
              >
                {correction.quantity}
              </QuantitySelector>
            </Box>
          </Box>
          <Box textAlign="center" sx={{ mt: 2, mr: 6 }}>
            {correction.description ? (
              <Box>
                <Typography>{correction.description}</Typography>
                <Typography sx={{ fontStyle: 'italic', fontSize: 12 }}>
                  {correction.color}/{correction.theme}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 'bold' }}>
                  {correction.vendor}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ color: 'gray' }}>
                Enter a sku to see details
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="success"
            disabled={isLoadingSku}
            startIcon={isLoadingSku ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isLoadingSku ? 'Loading' : 'Submit'}
          </Button>
          <Button onClick={handleCancel} variant="outlined" color="error">
            Cancel
          </Button>
        </DialogActions>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>
    </>
  );
}
